import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { Message } from '../_models/message.model';
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { environment } from 'src/environments/environment';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth';

declare let SockJS;
declare let Stomp;

@Injectable({ providedIn: 'root' })
export class MessageService {
  private authToken;
  private companyId;
  private userId;
  public stompClient;
  public msg = [];
  unreadMessageCount = 0;
  private loadCount = new BehaviorSubject<boolean>(false);
  $loadCount = this.loadCount.asObservable();

  constructor(
    private httpClient: HttpClient,
    private store: Store<AppState>,
    private httpUtils: HttpUtilsService,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => {
      this.authToken = res;
      this.store.pipe(select(currentUser)).subscribe((user) => {
        this.companyId = user.companyID;
        this.userId = user.id;
        if (this.userId && this.authToken) this.initializeWebSocketConnection();
      });
    });
  }

  initializeWebSocketConnection() {
    const serverUrl = environment.WEBSOCKET_URL;
    const ws = new SockJS(serverUrl);
    this.stompClient = Stomp.over(ws);
    this.stompClient.debug = null;

    this.stompClient.connect(
      { Authorization: 'Bearer ' + this.authToken },
      () => {
        this.stompClient.subscribe(`/topic/notifications/${this.userId}`, () => {
          this.loadCount.next(true);
        });
      },
      this.error,
    );
  }

  error() {
    setTimeout(() => {
      this.initializeWebSocketConnection();
    }, 5000);
  }

  createMessage(request, _message: Message) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      body: _message.body,
      receivers: _message.receiver,
      attachment_names: request.body.attachment_names,
      sender_name: request.body.sender_name,
      subject: request.body.subject,
      tags: _message.tags,
    };
    const params = new HttpParams({ fromString: 'communication_type=NOTIFICATION' });
    return this.httpClient.post<Message>(
      environment.TIMELINE_BASE + '/companies/' + this.companyId + '/communication',
      body,
      {
        headers: httpHeaders,
        observe: 'response',
        params,
      },
    );
  }

  uploadFiles(url, file): Observable<any> {
    return this.httpClient.put(url, file, {
      observe: 'events',
      reportProgress: true,
    });
  }

  sendFiles(id, uuids): Observable<any> {
    const url =
      environment.TIMELINE_BASE +
      '/companies/' +
      this.companyId +
      '/communication/' +
      id +
      '/attachments';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      uuids,
    };
    return this.httpClient.post<any>(url, body, { headers: httpHeaders });
  }

  findMessages(queryParams: QueryParamsModel): Observable<any> {
    const pageSize = queryParams.pageSize;
    const pageIndex = queryParams.pageNumber;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const params = new HttpParams({
      fromString:
        'communication_type=NOTIFICATION&receiver_type=USER&sort=updated,DESC&page=' +
        pageIndex +
        '&size=' +
        pageSize +
        '&receiver_id=' +
        this.userId,
    });
    return this.httpClient.get<QueryResultsModel>(
      `${environment.TIMELINE_BASE}/companies/${this.companyId}/communication`,
      { headers, params },
    );
  }

  loadUnreadMessageCount(): any {
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const params = new HttpParams({
      fromString:
        'communication_type=NOTIFICATION&receiver_type=USER&is_completed=false' +
        '&receiver_id=' +
        this.userId,
    });
    return this.httpClient.get<any>(
      `${environment.TIMELINE_BASE}/companies/${this.companyId}/communication/count`,
      { headers, params },
    );
  }

  unreadMessages(communication_id): Observable<any> {
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.httpClient.put<QueryResultsModel>(
      environment.TIMELINE_BASE +
        '/companies/' +
        this.companyId +
        '/communication/' +
        communication_id,
      { is_completed: true },
      { headers },
    );
  }

  setUnreadMessageCount(count) {
    this.unreadMessageCount = count;
  }

  getUnreadMessageCount() {
    return this.unreadMessageCount;
  }
}
